import { useEffect, useState } from "react";
import { useIdempotentSave } from "../../hooks/use-idempotent-save";
import { ChevronRight, HandCoins, Plus } from "lucide-react";
import type { DashboardData, IOU, Owner, Transaction } from "../../types";
import { money, todayIn } from "../../lib/finance";
import { api } from "../../lib/api";
import ConfirmDialog, { type ConfirmState } from "../../components/ConfirmDialog";
import PayeeAvatar from "../../components/PayeeAvatar";
import IouForm from "./IouForm";
import IouEditModal from "./IouEditModal";
import type { Need } from "../../lib/coverage";
import IouDetail from "./IouDetail";
import RepaidBar from "./RepaidBar";
import { blankIou, type IouDraft, type LoanEditDraft, type PaymentEditDraft, type SettleDraft } from "./iou-drafts";
import SecondaryHeading from "../../app/SecondaryHeading";
import Modal from "../../components/Modal";
import { enqueuePendingRecord, removePendingRecord, saveLoanOrQueue, saveRecordOrQueue } from "../../lib/pending-records";
import { syncPendingEntries } from "../../lib/sync-pending-entries";

export default function IOUs({ data, ensure, profile, onSaved, onOpenTransaction, focusId, onFocusHandled }: { data: DashboardData; ensure: (need: Need) => unknown; profile: Owner; onSaved: () => Promise<unknown>; onOpenTransaction: (t: Transaction) => void; focusId?: string | null; onFocusHandled?: () => void }) {
  const [iouDraft, setIouDraft] = useState<IouDraft | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [settling, setSettling] = useState<SettleDraft | null>(null);
  const [editingIou, setEditingIou] = useState<LoanEditDraft | null>(null);
  const [editingPayment, setEditingPayment] = useState<PaymentEditDraft | null>(null);
  // Shared with the expense form, which creates and settles loans too.
  const saveOnce = useIdempotentSave();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  // Arriving from a payee: open that loan straight away rather than leaving it to be found.
  useEffect(() => {
    if (!focusId) return;
    setDetailId(focusId);
    onFocusHandled?.();
  }, [focusId, onFocusHandled]);
  const activeAccounts = (data.accounts || []).filter((a) => a.active !== false);
  const payees = data.payees || [];
  const payeeName = (id: string) => {
    if (id.startsWith("local:")) return id.slice("local:".length) || "New payee";
    return payees.find((p) => p.id === id)?.name || "Unknown payee";
  };
  const ious = [...(data.ious || [])].sort((a, b) =>
    String(b.createdDate || "").localeCompare(String(a.createdDate || "")),
  );
  const detail = ious.find((i) => i.id === detailId) || null;
  const pendingDetail = !!detailId?.startsWith("local:loan:");
  function closeDetail() {
    setEditingIou(null);
    setEditingPayment(null);
    if (busy) return;
    setDetailId(null);
    setSettling(null);
    setError("");
  }
  async function saveIOU() {
    if (!iouDraft || busy) return;
    if (!iouDraft.payeeId) { setError("Choose or add a payee."); return; }
    if (!iouDraft.fundingAccountId) { setError("Choose the account the money moved through."); return; }
    setBusy(true); setError("");
    try {
      const result = await saveLoanOrQueue(profile, iouDraft, (draft) => saveOnce("ious/create", draft));
      setIouDraft(null);
      if (result.queued) {
        if (navigator.onLine) void syncPendingEntries().then(() => onSaved()).catch(() => undefined);
      } else await onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function saveSettlement() {
    if (!settling || busy) return;
    if (settling.iouId.startsWith("local:")) {
      setError("Wait for the IOU to sync before recording a repayment.");
      return;
    }
    setBusy(true); setError("");
    try {
      const body = { iouId: settling.iouId, amount: settling.amount, date: todayIn(), accountId: settling.accountId, notes: settling.notes };
      const id = crypto.randomUUID();
      const result = await saveRecordOrQueue(
        () => saveOnce("ious/settle", body),
        () => enqueuePendingRecord({ kind: "settle", id, owner: profile, body }),
      );
      setSettling(null);
      if (result.queued) {
        if (navigator.onLine) void syncPendingEntries().then(() => onSaved()).catch(() => undefined);
      } else await onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function deletePendingLoan() {
    if (!detailId?.startsWith("local:loan:") || busy) return;
    setBusy(true); setError("");
    try {
      removePendingRecord(detailId.slice("local:loan:".length));
      closeDetail();
      await onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function saveIouEdit() {
    if (!detail || !editingIou) return;
    setBusy(true); setError("");
    try {
      await api("ious/update", { iouId: detail.id, ...editingIou });
      await onSaved();
      setEditingIou(null);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function savePaymentEdit() {
    if (!editingPayment) return;
    setBusy(true); setError("");
    try {
      await api("ious/settlement/update", { settlementId: editingPayment.id, amount: editingPayment.amount, date: editingPayment.date, accountId: editingPayment.accountId, notes: editingPayment.notes });
      await onSaved();
      setEditingPayment(null);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function removePayment(settlementId: string) {
    setBusy(true); setError("");
    try {
      await api("ious/settlement/delete", { settlementId });
      await onSaved();
      setEditingPayment(null);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function removeIOU(iou: IOU) {
    setBusy(true); setError("");
    try {
      await api("ious/delete", { iouId: iou.id });
      await onSaved();
      closeDetail();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function writeOff(iou: IOU, reason?: string) {
    setBusy(true); setError("");
    try {
      await api("ious/write-off", { iouId: iou.id, reason });
      await onSaved();
      closeDetail();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <section className="panel account-settings">
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
      <SecondaryHeading title="IOUs" icon={<HandCoins size={15} />}>
        {!iouDraft && (
          <button type="button" className="primary small" onClick={() => { setIouDraft(blankIou()); setError(""); }}>
            <Plus size={13} strokeWidth={2.5} /> Add
          </button>
        )}
      </SecondaryHeading>
      {error && !detail && <div className="inline-error" role="alert">{error}</div>}
      {iouDraft && (
        <Modal title="New IOU" onClose={() => setIouDraft(null)}>
          <IouForm
            draft={iouDraft}
            setDraft={setIouDraft}
            payees={payees}
            accounts={activeAccounts}
            payeeName={payeeName}
            busy={busy}
            onSubmit={() => void saveIOU()}
            onCancel={() => setIouDraft(null)}
            onPayeeCreated={() => void onSaved()}
          />
        </Modal>
      )}
      <div className="iou-list">
        {ious.length ? ious.map((iou) => (
          <button key={iou.id} type="button" className={`iou-card${iou.status !== "Open" ? " closed" : ""}`} onClick={() => setDetailId(iou.id)}>
            <div className="iou-card-head">
              <span className="iou-card-payee">
                <PayeeAvatar id={iou.payeeId} name={payeeName(iou.payeeId)} size={30} />
                <span>
                  <strong>{iou.description}</strong>
                  <small>{payeeName(iou.payeeId)} · {iou.direction}</small>
                </span>
              </span>
              <span className="iou-card-aside">
                <span className={`iou-status iou-status-${iou.status.toLowerCase().replace(" ", "-")}`}>
                  {iou.id.startsWith("local:loan:") ? "Not synced" : iou.status}
                </span>
                <ChevronRight size={16} className="iou-card-chevron" aria-hidden="true" />
              </span>
            </div>
            <div className="iou-card-amounts">
              {iou.status === "Open" ? (
                <span>
                  <strong>{money(iou.outstanding, iou.currency)}</strong> left of {money(iou.originalAmount, iou.currency)}
                </span>
              ) : (
                <span>
                  <strong>{money(iou.originalAmount, iou.currency)}</strong>
                  {iou.status === "Settled" ? " settled" : " written off"}
                </span>
              )}
            </div>
            {iou.status === "Open" && !iou.id.startsWith("local:loan:") && <RepaidBar original={iou.originalAmount} outstanding={iou.outstanding} />}
          </button>
        )) : (
          <div className="empty-small iou-empty">
            <HandCoins size={28} />
            <p>No IOUs logged yet. Track money you’ve lent or borrowed.</p>
          </div>
        )}
      </div>
      {detail && (editingIou || editingPayment) && (
        <IouEditModal
          detail={detail}
          accounts={activeAccounts}
          editingIou={editingIou}
          setEditingIou={setEditingIou}
          editingPayment={editingPayment}
          setEditingPayment={setEditingPayment}
          error={error}
          setError={setError}
          busy={busy}
          setConfirm={setConfirm}
          onSaveLoan={() => void saveIouEdit()}
          onSavePayment={() => void savePaymentEdit()}
          onRemovePayment={(id) => removePayment(id)}
        />
      )}
      {detail && pendingDetail && (
        <Modal title="Not synced" onClose={closeDetail}>
          <p>Saved on this device. It will sync automatically when you’re online.</p>
          <p><strong>{detail.description}</strong> · {payeeName(detail.payeeId)}</p>
          {error && <div className="inline-error" role="alert">{error}</div>}
          <button
            type="button"
            className="text-button subtle danger"
            disabled={busy}
            onClick={() =>
              setConfirm({
                title: "Delete unsynced IOU?",
                message: "This IOU was saved on this device and hasn’t been synced yet. Deleting it will remove it completely.",
                confirmLabel: "Delete IOU",
                danger: true,
                onConfirm: () => deletePendingLoan(),
              })
            }
          >
            Delete unsynced IOU
          </button>
        </Modal>
      )}
      {detail && !pendingDetail && !editingIou && !editingPayment && (
        <IouDetail
          detail={detail}
          data={data}
          ensure={ensure}
          accounts={activeAccounts}
          payeeName={payeeName}
          error={error}
          setError={setError}
          busy={busy}
          settling={settling}
          setSettling={setSettling}
          setEditingIou={setEditingIou}
          setEditingPayment={setEditingPayment}
          setConfirm={setConfirm}
          onOpenTransaction={onOpenTransaction}
          onClose={closeDetail}
          onSaveSettlement={() => void saveSettlement()}
          onWriteOff={(reason?: string) => writeOff(detail, reason)}
          onDelete={() => removeIOU(detail)}
        />
      )}
    </section>
  );
}
